import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import ProjectCard from "@/components/ProjectCard";
import ProjectsMap from "@/components/ProjectsMap";
import api from "@/lib/api";
import { Loader2, Search, LayoutGrid, Map as MapIcon } from "lucide-react";
import { Input } from "@/components/ui/input";

const SECTORS = ["Tous", "Énergie", "Agriculture", "Infrastructure", "Mines", "Technologie", "Numérique", "Immobilier", "Santé"];

export default function Catalogue() {
  const [params, setParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sector, setSector] = useState("Tous");
  const [q, setQ] = useState("");
  const view = params.get("view") === "map" ? "map" : "grid";
  const setView = (v) => setParams(v === "map" ? { view: "map" } : {});

  useEffect(() => {
    setLoading(true);
    const p = sector === "Tous" ? {} : { sector };
    api.get("/projects", { params: p }).then(({ data }) => setProjects(data)).finally(() => setLoading(false));
  }, [sector]);

  const filtered = projects.filter((p) => p.title.toLowerCase().includes(q.toLowerCase()) || p.location.toLowerCase().includes(q.toLowerCase()));

  let content;
  if (loading) {
    content = <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-congo-blue" /></div>;
  } else if (filtered.length === 0) {
    content = <p className="text-center py-20 text-slate-500" data-testid="no-projects">Aucun projet trouvé.</p>;
  } else if (view === "map") {
    content = <div className="mt-6"><ProjectsMap projects={filtered} /></div>;
  } else {
    content = (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6" data-testid="projects-grid">
        {filtered.map((p) => <ProjectCard key={p.id} p={p} />)}
      </div>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-congo-blue">Catalogue</p>
        <h1 className="font-head text-3xl font-bold text-slate-900 mt-2">Projets nationaux à financer</h1>
        <p className="text-slate-600 mt-2">Sélectionnés et validés par la CADECO. Investissez dès 50$.</p>

        <div className="relative mt-6 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un projet ou une ville…" className="h-12 rounded-full pl-10" />
        </div>

        <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
          <div className="flex gap-2 overflow-x-auto hide-scrollbar snap-x flex-1">
            {SECTORS.map((s) => (
              <button key={s} onClick={() => setSector(s)} data-testid={`filter-${s}`}
                className={`shrink-0 snap-start px-4 py-2 rounded-full text-sm font-medium transition-colors ${sector === s ? "bg-congo-blue text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-congo-blue"}`}>
                {s}
              </button>
            ))}
          </div>
          <div className="flex bg-white border border-slate-200 rounded-full p-1 shrink-0">
            <button onClick={() => setView("grid")} data-testid="view-grid" className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${view === "grid" ? "bg-congo-blue text-white" : "text-slate-600"}`}><LayoutGrid className="h-4 w-4" /> Liste</button>
            <button onClick={() => setView("map")} data-testid="view-map" className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${view === "map" ? "bg-congo-blue text-white" : "text-slate-600"}`}><MapIcon className="h-4 w-4" /> Carte</button>
          </div>
        </div>

        {content}
      </div>
    </Layout>
  );
}
